import { observer } from "mobx-react-lite";

import { Group } from "@/components/expression/utils/groups/Group";
import { useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { twMerge } from "@/utils/twMerge";

import { Decorators } from "./utils/decorators/Decorators";
import { useIsSelected } from "./utils/layout";

import type { GroupSlice } from "./inline-node/wrapping/types";
import type { ExpressionComponent } from "./types";
import type { DecoratorNodeModel } from "@/models/nodes/DecoratorNodeModel";
import type { ReactNode } from "react";

type Props = {
  slice?: GroupSlice;
  children?: ReactNode;
};

export const GroupNode: ExpressionComponent<"group", Props> = observer(function GroupNode({
  node,
  className,
  nodeRenderProps,
  slice,
  children,
}) {
  const { doc, openingSymbol, closingSymbol } = node;
  const { view } = useNodeStyle();
  const isSelected = useIsSelected(node);

  if (!node.nodes.length) return null;

  const hasBoxedDecorator = node.decorators.some((decorator) => decorator.name === "boxed");
  const hideParentheses = node.omitParentheses && (hasBoxedDecorator || !doc.cursor.isOnNode(node));
  const isTextView = view === "text";

  return (
    <Decorators
      decorators={node.decorators}
      isSelected={isSelected}
      className={twMerge(
        !isTextView && getDecoratorMargins(node.decorators),

        hasBoxedDecorator && "p-1.5",
        hasBoxedDecorator && isTextView && "py-0 px-0.5",
      )}
      wrapperClassName={twMerge(
        "mx-1.5 ",

        hideParentheses && "mx-[1px]",

        !hasBoxedDecorator && "-top-[3px] -bottom-[3px]",
        hasBoxedDecorator && "top-0 bottom-0 mx-0",

        isTextView && !hasBoxedDecorator && "-top-[1px] -bottom-[1px]",
      )}
    >
      <Group
        node={node}
        nodePlaceholders={nodeRenderProps?.placeholders}
        parameters={node.inlineNode ? [node.inlineNode] : node.nodes}
        openingSymbol={openingSymbol}
        closingSymbol={closingSymbol}
        disabled={node.omitParentheses}
        className={className}
        hideParentheses={hideParentheses}
        hideMissingParentheses={node.casesSystemEnabled}
        isHighlighted={node.isHighlighted}
        slice={slice}
      >
        {children}
      </Group>
    </Decorators>
  );
});

function getDecoratorMargins(decorators: DecoratorNodeModel[]): string {
  let topMarginClass = "";
  let bottomMarginClass = "";

  const hasOverDecorators = decorators.find(
    (dec) => dec.name.includes("over") || dec.name.includes("wide"),
  );
  const hasUnderDecorators = decorators.find((dec) => dec.name.includes("under"));

  if (hasOverDecorators) {
    topMarginClass = "mt-1.5";

    if (decorators.find((dec) => dec.name === "overbrace" || dec.name === "widehat")) {
      topMarginClass = "mt-2.5";
    }
  }

  if (hasUnderDecorators) {
    bottomMarginClass = "mb-1.5";

    if (decorators.find((dec) => dec.name === "underbrace")) {
      bottomMarginClass = "mb-2.5";
    }
  }

  return `${topMarginClass} ${bottomMarginClass}`.trim();
}
